/**
 * Milestone Checker Service
 *
 * Evaluates milestone conditions against current user context.
 * Tracks achieved milestones in localStorage (no DB needed).
 * Returns newly achieved milestones for celebration toasts.
 */

import { MILESTONES } from '@/types/xp';
import type { MilestoneCheckContext, Milestone } from '@/types/xp';

const STORAGE_KEY_PREFIX = 'converlang_milestones_achieved_';

/**
 * Get the localStorage key for a user's achieved milestones.
 */
function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Get the set of achieved milestone IDs from localStorage.
 */
export function getAchievedMilestones(userId: string): Set<string> {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (!stored) return new Set();
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

/**
 * Save achieved milestone IDs to localStorage.
 */
function saveAchievedMilestones(userId: string, achieved: Set<string>): void {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify([...achieved]));
  } catch {
    // localStorage may be full or unavailable
  }
}

/**
 * Check all milestones against the current context.
 * Returns only NEWLY achieved milestones (not previously achieved).
 *
 * @param userId - Current user ID (for localStorage lookup)
 * @param context - Current milestone evaluation context
 * @returns Array of newly achieved milestones
 */
export function checkMilestones(
  userId: string,
  context: MilestoneCheckContext
): Milestone[] {
  const achieved = getAchievedMilestones(userId);
  const newlyAchieved: Milestone[] = [];

  for (const milestone of MILESTONES) {
    // Skip already achieved
    if (achieved.has(milestone.id)) continue;

    // Check condition
    if (milestone.condition(context)) {
      newlyAchieved.push(milestone);
      achieved.add(milestone.id);
    }
  }

  // Persist updated achieved set
  if (newlyAchieved.length > 0) {
    saveAchievedMilestones(userId, achieved);
  }

  return newlyAchieved;
}

/**
 * Get count of total achieved milestones for display.
 */
export function getAchievedCount(userId: string): number {
  return getAchievedMilestones(userId).size;
}

/**
 * Check if a specific milestone has been achieved.
 */
export function isMilestoneAchieved(userId: string, milestoneId: string): boolean {
  return getAchievedMilestones(userId).has(milestoneId);
}
